import React from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import InfoTooltipButton from "./InfoTooltipButton";

import ReactTable from "../../components/ReactTable/ReactTable.js";
import CardHeader from "../../components/Card/CardHeader";
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import useCustomStyles from "../../customAssets/customStyles";
import CardText from "../../components/Card/CardText";

import { useTranslation } from "react-i18next";

/**
 * Estilos locales del bloque de análisis del Manifest.
 *
 * Objetivos de esta versión:
 * - evitar posiciones absolutas frágiles en el header
 * - mantener visible el icono de información
 * - mejorar el comportamiento responsive del bloque
 * - permitir scroll horizontal controlado si la tabla lo necesita
 * - forzar wrap correcto en textos largos
 */
const useStyles = makeStyles((theme) => ({
  pageSubcategoriesTitle: {
    color: "#3C4858",
    textDecoration: "none",
    textAlign: "center",
  },

  cardCategory: {
    margin: 0,
    color: "#999999",
  },

  /**
   * La pastilla visual real del header es CardText.
   * El contenido del título y el icono deben vivir dentro.
   */
  headerBadge: {
    display: "block",
    width: "fit-content",
    maxWidth: "100%",
  },

  /**
   * Cabecera responsive dentro de la propia pastilla turquesa.
   */
  headerContent: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    minWidth: 0,
    maxWidth: "100%",
  },

  headerTitleWrap: {
    flex: 1,
    minWidth: 0,
  },

  headerActions: {
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
  },

  tooltip: {
    fontSize: "0.9rem",
    padding: "12px 16px",
    maxWidth: 320,
  },

  arrow: {},

  /**
   * Botón circular de ayuda, con caja fija y centrado real.
   */
  infoIconWrapper: {
    width: 30,
    height: 30,
    padding: 0,
    color: "#fff",
    backgroundColor: "rgba(255, 255, 255, 0.16)",
    borderRadius: "50%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 0,
    flexShrink: 0,
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.24)",
    },
  },

  /**
   * Ajuste fino para que la "i" no quede baja dentro del círculo.
   */
  infoIcon: {
    width: 18,
    height: 18,
    fontSize: 18,
    display: "block",
    position: "relative",
    top: "-2px",
  },

  cardBody: {
    padding: theme.spacing(2),

    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.5),
    },
  },

  tableWrapper: {
    width: "100%",
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
  },

  cellText: {
    wordBreak: "break-word",
    overflowWrap: "anywhere",
    whiteSpace: "normal",
    lineHeight: 1.5,
  },
}));

const HEADER_INFO = {
  manifest: "customtable-tooltip-manifest",
};

function ManifestHtmlCell({ cell }) {
  const value = cell?.value || "";
  const classes = useStyles();

  return (
    <div
      className={classes.cellText}
      dangerouslySetInnerHTML={{ __html: value }}
    />
  );
}

ManifestHtmlCell.propTypes = {
  cell: PropTypes.shape({
    value: PropTypes.any,
  }),
};

function ManifestTextCell({ cell }) {
  const value = cell?.value || "";
  const classes = useStyles();

  return <div className={classes.cellText}>{value}</div>;
}

ManifestTextCell.propTypes = {
  cell: PropTypes.shape({
    value: PropTypes.any,
  }),
};

const ManifestInfo = ({ record, infoKey }) => {
  const { t } = useTranslation("search");
  const classes = useStyles();
  const customClasses = useCustomStyles();
  const mergedClasses = { ...classes, ...customClasses };

  if (
    !record ||
    !Array.isArray(record.manifest_analysis) ||
    record.manifest_analysis.length === 0
  ) {
    return null;
  }

  const manifestRows = record.manifest_analysis.map((details, index) => ({
    id: index,
    name: details?.title || "",
    status: details?.severity || "",
    description: details?.description || "",
  }));

  const manifestColumns = [
    {
      Header: "Issue",
      accessor: "name",
      Cell: ManifestHtmlCell,
    },
    {
      Header: "Severity",
      accessor: "status",
      Cell: ManifestTextCell,
    },
    {
      Header: "Description",
      accessor: "description",
      Cell: ManifestTextCell,
    },
  ];

  const tooltipKey = infoKey && HEADER_INFO[infoKey] ? HEADER_INFO[infoKey] : null;
  const tooltipText = tooltipKey ? t(tooltipKey, { defaultValue: "" }) : "";

  return (
    <GridContainer>
      <GridItem xs={12}>
        <Card>
          <CardHeader color="success" icon>
            <CardText color="primary" className={classes.headerBadge}>
              <div className={classes.headerContent}>
                <div className={classes.headerTitleWrap}>
                  <h4 className={mergedClasses.cardTitle}>
                    {t("category-title-manifest")}
                  </h4>
                </div>

                {tooltipText ? (
                  <div className={classes.headerActions}>
                    <InfoTooltipButton
                      title={tooltipText}
                      placement="right"
                      tooltipClasses={{
                        tooltip: classes.tooltip,
                        arrow: classes.arrow,
                      }}
                      iconButtonClassName={classes.infoIconWrapper}
                      iconClassName={classes.infoIcon}
                    />
                  </div>
                ) : null}
              </div>
            </CardText>
          </CardHeader>

          <CardBody className={classes.cardBody}>
            <div className={classes.tableWrapper}>
              <ReactTable data={manifestRows} columns={manifestColumns} />
            </div>
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
};

ManifestInfo.propTypes = {
  record: PropTypes.shape({
    manifest_analysis: PropTypes.array,
  }),
  infoKey: PropTypes.string,
};

export default ManifestInfo;